import { getServerDate } from "../utils/storage/date";
import { getCurrentVehicle } from "../utils/device";
import { NODEVICEID, NOTRAILERID } from "./DutyStatusLog";
import { DataActions } from "../store/data/actions";
import { PictureSourceType, getFileController, IImageResult } from "../utils/native/nativeFileApi";
import { dispatcher, DispatcherEvents } from "../utils/dispatcher";
import { DriveStore } from "../store/reducers";
import { IImages } from "../store/data/types";
import { AppType, getAppType } from "../utils/native/nativeHelpers";
import constants from "../constants";
import * as logger from "../utils/logger";
import type { IDVIRLog } from "./DVIRLog";

export interface IPicture {
    id: string;
    fromDate: string;
    deviceId: IIdEntity | string;
}

export interface IMediaFile {
    id: string;
    defectId: string;
    isUploadedToCloud: boolean;
    path: string;
    modified?: string;
    isLinkedWithRemark: boolean;
}

export interface IImageOptions {
    cameraIconId: string;
    base64Image: string;
    deleteImage: boolean;
    filePath: string;
}

export function getImage(filePath: string, isUploadedToCloud = false, defectId = "", isAdding = true, isLinkedWithRemark = false, isTrailer = false): IPicture & IMediaFile {
    return {
        id: filePath,
        defectId: defectId,
        fromDate: getServerDate().toISOString(),
        deviceId: !isTrailer ? getCurrentVehicle() as IIdEntity : NODEVICEID,
        isUploadedToCloud: isUploadedToCloud,
        path: filePath,
        modified: isAdding ? "Add" : "",
        isLinkedWithRemark: isLinkedWithRemark
    };
}

/**
 * True for a photo whose bytes have not reached the server: either the MediaFile entity has not
 * been added yet (`modified`) or the binary has not been uploaded (`isUploadedToCloud`).
 *
 * The device holds the only copy of such a photo, so session cleanup and the drvfs sweep must not
 * destroy it — for a defect photo the evidentiary part is the photo (MYG-144154, completing the
 * inspection retention of MYG-143880).
 *
 * Photos fetched back from the server are correctly excluded: dvir.ts builds those with
 * `isUploadedToCloud: true` and `modified: ""`, so they stay disposable cache.
 */
export const isMediaPendingDelivery = (image?: DeepReadonlyObject<IPicture & IMediaFile> | (IPicture & IMediaFile)): boolean => {
    return Boolean(image) && (Boolean(image?.modified) || !image?.isUploadedToCloud);
};

/**
 * Ids of the photos in `images` whose bytes have not reached the server. Exported so callers
 * reporting on retention use the same predicate the retention filter keeps by — if the two drift,
 * a caller ends up describing a set that does not match what was actually kept.
 */
export const getMediaPendingDeliveryIds = (images?: DeepReadonlyObject<IImages>): string[] => {
    return Object.keys(images || {}).filter(id => isMediaPendingDelivery(images?.[id]));
};

export const hasMediaPendingDelivery = (images?: DeepReadonlyObject<IImages>): boolean => {
    return getMediaPendingDeliveryIds(images).length > 0;
};

/**
 * True when `image` is a photo of one of `log`'s defects.
 *
 * Nothing on IDVIRLog references a media id, so this pair of comparisons is the only link between
 * the two, and dvir.ts resolves it afresh on every push. Both halves mirror that code exactly:
 *
 * - asset: a vehicle log matches images carrying that vehicle's id, a trailer log matches images
 *   captured with no device (see the `filteredByAssetImages` filter in dvir.ts).
 * - defect: which id the image carries depends on where it was captured. The signing flow labels
 *   the camera icon with the defect *template* id (inspectionList.ts), while the repair flow uses
 *   the server-assigned DVIRDefect id (inspect.ts). `modified` tells the two apart, the same way
 *   updateRepairRemarkWithMediaFile does.
 */
export const isMediaForDvirLog = (
    image: DeepReadonlyObject<IPicture & IMediaFile> | (IPicture & IMediaFile),
    log?: DeepReadonlyObject<IDVIRLog> | IDVIRLog
): boolean => {
    if (!log || !log.dVIRDefects || !log.dVIRDefects.length) {
        return false;
    }

    const isTrailerLog = Boolean(log.trailer && log.trailer.id !== NOTRAILERID);
    const logDeviceId = isTrailerLog ? undefined : log.device?.id;
    const matchesAsset = logDeviceId
        ? (image.deviceId as IIdEntity)?.id === logDeviceId
        : image.deviceId === NODEVICEID;

    if (!matchesAsset) {
        return false;
    }

    return log.dVIRDefects.some(defect => {
        const defectId = log.modified === "Add" ? defect.defect?.id : defect.id;
        return Boolean(defectId) && defectId === image.defectId;
    });
};

export function convertToBinary (base64: string): ArrayBuffer {
    let length = base64.length;
    let buf = new ArrayBuffer(length);
    let arr = new Uint8Array(buf);
    for (let i = 0; i < length; i++) {
      arr[i] = base64.charCodeAt(i);
    }
    return buf;
}

function addNewImageToStore(store: DriveStore, filePath: string, cameraIconId = "", isAdding = true, isLinkedWithRemark = false, mediaId = "", isTrailer = false) {
    let images = store.getState().data.images;
    let newImage = getImage(filePath, false, cameraIconId.substring(0, cameraIconId.indexOf("_camera")), isAdding, isLinkedWithRemark, isTrailer);
    store.dispatch(DataActions.setImages({
        ...images,
        [mediaId]: newImage
    }));
    dispatcher.trigger(DispatcherEvents.ImageAdded, { filePath: filePath, cameraIconId: cameraIconId, mediaFileId: filePath });
}

function deleteImageFromStore(store: DriveStore, filePath: string) {
    let images = store.getState().data.images;
    let newImages: IImages = Object.keys(images).reduce((result: IImages, key: string) => {
        if (images[key].path !== filePath) {
            result[key] = images[key];
        }
        return result;
    }, {});
    store.dispatch(DataActions.setImages(newImages));
}

export function getAndSaveNewImage(source: PictureSourceType, store: DriveStore, cameraIconId = "", isTrailer = false, cb: ((imageResult: IImageResult) => void) | null = null): Promise<string> {
    return getFileController().getPicture(source).then((mediaData) => {
        if (cb) {
            cb(mediaData);
            return "";
        } else {
            addNewImageToStore(store, mediaData.filePath, cameraIconId, true, false, mediaData.mediaId, isTrailer);
            return mediaData.filePath;
        }
    });
}

export function deleteImage(store: DriveStore, filePath: string): Promise<string> {
    deleteImageFromStore(store, filePath);
    if (window.driveApp?.debug?.base64TestImage){
        return Promise.resolve("");
    }
    return getFileController().removeFile(filePath);
}

/**
 * Deletes drvfs media files whose modifiedDate is older than MEDIA_FILE_RETENTION_DAYS days.
 * No-ops on Cordova and web — drvfs is NativeSDK only.
 * Errors per file are logged and swallowed so cleanup never blocks logout.
 */
export async function cleanupOldDrvfsFiles(store: DriveStore): Promise<void> {
    if (getAppType() !== AppType.NativeSdk) {
        return;
    }

    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - constants.MEDIA_FILE_RETENTION_DAYS);

    const fileController = getFileController();
    let files;
    try {
        files = await fileController.list(constants.DRVFS_ROOT);
    } catch (e) {
        logger.error(logger.Source.SYNC, `Logout cleanup: failed to list drvfs files: ${ (e as Error).message }`);
        return;
    }

    // Files still owed to the server. Retaining a queue entry through session cleanup is worthless
    // if this sweep deletes the file it points at, so a photo awaiting upload outlives the cutoff
    // (MYG-144154). Anything already delivered stays disposable, whatever its age.
    //
    // Matched on file name, not full path: the two strings are produced independently — the stored
    // path comes from the native picker callback, the swept one is rebuilt from a list() entry —
    // and the cost of them disagreeing is asymmetric. A false match keeps a stale file until the
    // next sweep; a false miss deletes the only copy of a photo the server has never seen, which is
    // the outcome this guard exists to prevent. DRVFS_ROOT is flat and directories are skipped
    // below, so a name identifies a file here.
    const images = store.getState().data.images;
    const pendingFileNames = new Set(
        getMediaPendingDeliveryIds(images)
            .map(id => images[id].path)
            .filter(Boolean)
            .map(path => path.substring(path.lastIndexOf("/") + 1))
    );

    const filesToDelete: typeof files = [];
    let mediaFileCount = 0;
    let skippedPendingCount = 0;
    let oldestFile: typeof files[0] | undefined;

    const mediaExtensions = /\.(png|jpg|jpeg)$/i;

    for (const file of files) {
        if (file.isDir || !mediaExtensions.test(file.name)) {
            continue;
        }
        mediaFileCount++;
        // Records a sighting, not a sparing: a file the cutoff leaves alone has been seen just as
        // much as one the cutoff would delete. Removing it only in the branch below would leave
        // every photo newer than the cutoff in the set, and the report at the end of this function
        // would then name it as missing while it sits right there — which is the ordinary case
        // here, a driver signing offline and logging out the same shift.
        const isPendingUpload = pendingFileNames.delete(file.name);
        const modDate = new Date(file.modifiedDate);
        if (!oldestFile || modDate < new Date(oldestFile.modifiedDate)) {
            oldestFile = file;
        }
        if (modDate < cutoffDate) {
            if (isPendingUpload) {
                skippedPendingCount++;
                continue;
            }
            filesToDelete.push(file);
        }
    }

    if (mediaFileCount > 0) {
        logger.log(
            logger.Source.STORAGE,
            `Logout cleanup: ${ mediaFileCount } media file(s) found. Oldest: ${ oldestFile ? `${ oldestFile.name } (modified: ${ oldestFile.modifiedDate })` : "none" }`
        );
    }

    if (skippedPendingCount > 0) {
        // Named rather than silent: these files are the only copy of a photo the server has never
        // seen, and a sweep that quietly spares them is as hard to audit as one that quietly
        // deletes them.
        logger.log(
            logger.Source.STORAGE,
            `Logout cleanup: ${ skippedPendingCount } drvfs file(s) past the cutoff kept — still awaiting upload`
        );
    }

    // Every name left in the set belongs to a photo the queue still expects to upload but that the
    // sweep never saw on disk. The upload will fail when it cannot read the file, so surface the
    // gap here where the queue and the filesystem can still be compared, rather than leaving it to
    // be inferred from a later read error.
    if (pendingFileNames.size > 0) {
        logger.error(
            logger.Source.STORAGE,
            `Logout cleanup: ${ pendingFileNames.size } media file(s) awaiting upload were not found in drvfs: ${ Array.from(pendingFileNames).join(", ") }.`
        );
    }

    let deletedCount = 0;
    for (const file of filesToDelete) {
        const filePath = `${ constants.DRVFS_ROOT }${ file.name }`;
        try {
            await deleteImage(store, filePath);
            deletedCount++;
            logger.log(logger.Source.DEBUG, `Logout cleanup: removed ${ file.name } (modified: ${ file.modifiedDate })`);
        } catch (e) {
            logger.error(logger.Source.STORAGE, `Logout cleanup: failed to remove ${ file.name }: ${ (e as Error).message }`);
        }
    }
    if (deletedCount > 0) {
        logger.log(logger.Source.STORAGE, `Logout cleanup: ${ deletedCount } of ${ filesToDelete.length } drvfs file(s) deleted`);
    }
}